import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { api } from './hooks/useApi';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import ProductionSaumon from './pages/ProductionSaumon';
import ProductionRibs from './pages/ProductionRibs';
import ProductionPorc from './pages/ProductionPorc';
import PorcRecommandation from './pages/PorcRecommandation';
import ProductionSaucisses from './pages/ProductionSaucisses';
import RecetteSaucisses from './pages/RecetteSaucisses';
import ProductionSauce from './pages/ProductionSauce';
import RecetteSauce from './pages/RecetteSauce';
import ProductionTallow from './pages/ProductionTallow';
import Commandes from './pages/Commandes';
import Login from './pages/Login';
import InventaireMatieres from './pages/InventaireMatieres';
import InventaireProduits from './pages/InventaireProduits';
import ProductionBrisket from './pages/ProductionBrisket';
import FicheTracabilite from './pages/FicheTracabilite';
import JournalComptable from './pages/JournalComptable';
import DepensesPerso from './pages/DepensesPerso';
import RegistreActifs from './pages/RegistreActifs';
import EtatResultats from './pages/EtatResultats';
import AvancesFonds from './pages/AvancesFonds';
import TaxesTVQ from './pages/TaxesTVQ';
import Settings from './pages/Settings';
import AgentDebug from './pages/AgentDebug';
import Evenements from './pages/Evenements';
import EvenementDetail from './pages/EvenementDetail';
import Catalogue from './pages/Catalogue';
import CatalogueProduit from './pages/CatalogueProduit';
import ContratsList from './pages/ContratsList';
import ContratCorpo from './pages/ContratCorpo';
import ClientsList from './pages/ClientsList';
import TermesConditions from './pages/TermesConditions';
import PrixCatalogue from './pages/PrixCatalogue';
import Analytics from './pages/Analytics';
import EmailCentral from './pages/EmailCentral';
import JourJ from './pages/JourJ';
import TemperaturesDashboard from './pages/TemperaturesDashboard';
import AnalyseTemperatures from './pages/AnalyseTemperatures';
import QRScanner from './components/QRScanner';
import ScannerRecu from './components/ScannerRecu';
// Note: index.css + App.css sont importés dans index.js AVANT App
// (la base design system doit charger avant le CSS page-local).

function PrivateRoute({ children }) {
  const token = localStorage.getItem('escamel_token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toasts, setToasts]           = useState([]);
  const [alertes, setAlertes]         = useState([]);
  const navigate = useNavigate();

  // Favicon — cache-bust au démarrage pour forcer le rechargement
  useEffect(() => {
    const link = document.getElementById('dynamic-favicon');
    if (link) link.href = `/api/parametres/general/favicon?t=${Date.now()}`;
  }, []);

  // ─── Calcul jours avant 31 déc ────────────────
  function joursAvant31Dec() {
    const now = new Date();
    const fin = new Date(now.getFullYear(), 11, 31);
    return Math.max(0, Math.round((fin - now) / 86400000));
  }

  function ajouterToast(toast) {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { ...toast, id }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 6000);
  }

  useEffect(() => {
    const token = localStorage.getItem('escamel_token');
    if (!token) return;

    async function verifierAlertes() {
      const nouvelles = [];

      // 1. Solde API Claude
      try {
        const res = await api.get('/parametres/api-claude');
        if (res?.alerte?.active) {
          nouvelles.push({
            niveau: 'danger',
            message: `Solde API Claude bas : ${res.usage_local.solde_estime_restant.toFixed(2)}\u00a0$ restant`,
            lien: '/parametres?tab=api',
          });
        }
      } catch {}

      // 2. Stocks sous seuil
      try {
        const stocks = await api.get('/inventaire/alertes');
        if (Array.isArray(stocks) && stocks.length > 0) {
          nouvelles.push({
            niveau: 'warning',
            message: `${stocks.length} matière(s) sous seuil d'alerte`,
            lien: '/inventaire/matieres',
          });
        }
      } catch {}

      // 3. Clôture d'année
      try {
        const config = await api.get('/parametres/alertes');
        if (config?.alerte_cloture_annee) {
          const jours = joursAvant31Dec();
          if (jours <= (config.rappel_cloture_jours_avant || 30)) {
            nouvelles.push({
              niveau: 'info',
              message: `Clôture FY dans ${jours} jour(s) — 31 déc.`,
              lien: '/comptabilite/resultats',
            });
          }
        }
      } catch {}

      setAlertes(nouvelles);

      // Afficher max 2 toasts au chargement
      nouvelles.slice(0, 2).forEach((a, i) => {
        setTimeout(() => ajouterToast(a), i * 800);
      });
    }

    verifierAlertes();
  }, []); // eslint-disable-line

  const couleurToast = (niveau) => {
    if (niveau === 'danger')  return 'rgba(192,57,43,0.95)';
    if (niveau === 'warning') return 'rgba(217,119,6,0.95)';
    return 'rgba(37,99,235,0.95)';
  };

  return (
    <div className="app-shell">
      {/* Topbar mobile */}
      <div className="mobile-topbar">
        <button className="hamburger" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <span /><span /><span />
        </button>
        <div className="mobile-logo">
          <img src="/logo.png" alt="Escamel" style={{ width: 26, height: 26, objectFit: 'contain' }} />
          <span>ESCAMEL</span>
        </div>
        <button className="mobile-scan-btn" onClick={() => navigate('/scan')} title="Scanner un batch" aria-label="Scanner">
          &#x1F4F7;
        </button>
      </div>

      {/* Backdrop */}
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} nbAlertes={alertes.length} />
      <ScannerRecu />
      {/* Toasts alertes */}
      <div style={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 9999 }}>
        {toasts.map(t => (
          <div
            key={t.id}
            onClick={() => t.lien && navigate(t.lien)}
            style={{
              background: couleurToast(t.niveau),
              color: '#fff', borderRadius: 8,
              padding: '10px 18px', fontSize: 13,
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              cursor: t.lien ? 'pointer' : 'default',
              animation: 'fadeIn 0.3s',
              maxWidth: 360,
            }}
          >
            {t.niveau === 'danger' ? '🔴' : t.niveau === 'warning' ? '⚠️' : 'ℹ️'} {t.message}
          </div>
        ))}
      </div>
      <main className="app-content">
        <Routes>
          <Route path="/"                          element={<Dashboard />} />
          <Route path="/production/saumon"         element={<ProductionSaumon />} />
          <Route path="/production/ribs"           element={<ProductionRibs />} />
          <Route path="/production/porc"           element={<ProductionPorc />} />
          <Route path="/porc/recommandation"       element={<PorcRecommandation />} />
          <Route path="/production/brisket"        element={<ProductionBrisket />} />
          <Route path="/batches/:id/tracabilite"   element={<FicheTracabilite />} />
          {/* Burnt Ends produits via le module Brisket (répartition post-cuisson) — redirige */}
          <Route path="/production/burnt-ends"     element={<Navigate to="/production/brisket" replace />} />
          <Route path="/production/saucisses"      element={<ProductionSaucisses />} />
          <Route path="/saucisses/recette"          element={<RecetteSaucisses />} />
          <Route path="/production/sauce"           element={<ProductionSauce />} />
          <Route path="/sauce/recette"              element={<RecetteSauce />} />
          <Route path="/production/tallow"          element={<ProductionTallow />} />
          <Route path="/temperatures"              element={<TemperaturesDashboard />} />
          <Route path="/temperatures/analyse"     element={<AnalyseTemperatures />} />
          <Route path="/inventaire/matieres"       element={<InventaireMatieres />} />
          <Route path="/inventaire/produits"       element={<InventaireProduits />} />
          <Route path="/catalogue"               element={<Catalogue />} />
          <Route path="/catalogue/:id"           element={<CatalogueProduit />} />
          <Route path="/evenements"                element={<Evenements />} />
          <Route path="/evenements/:id"            element={<EvenementDetail />} />
          <Route path="/corpo"                     element={<ContratsList />} />
          <Route path="/corpo/contrats/:id"        element={<ContratCorpo />} />
          <Route path="/corpo/clients"             element={<ClientsList />} />
          <Route path="/corpo/termes"              element={<TermesConditions />} />
          <Route path="/corpo/prix-catalogue"      element={<PrixCatalogue />} />
          <Route path="/evenements/:id/jour-j"     element={<JourJ />} />
          <Route path="/commandes"                 element={<Commandes />} />
          <Route path="/analytics"                 element={<Analytics />} />
          <Route path="/emails"                    element={<EmailCentral />} />
          <Route path="/comptabilite/journal"          element={<JournalComptable />} />
          <Route path="/comptabilite/resultats"        element={<EtatResultats />} />
          <Route path="/comptabilite/taxes"            element={<TaxesTVQ />} />
          <Route path="/comptabilite/depenses-perso"   element={<DepensesPerso />} />
          <Route path="/comptabilite/avances"          element={<AvancesFonds />} />
          <Route path="/comptabilite/actifs"           element={<RegistreActifs />} />
          <Route path="/scan"                          element={<QRScanner />} />
          <Route path="/parametres"                    element={<Navigate to="/settings" replace />} />
          <Route path="/settings"                      element={<Settings />} />
          <Route path="/agent-debug"                   element={<AgentDebug />} />
          <Route path="*" element={
            <div style={{ padding: '40px', color: 'var(--muted)' }}>Page introuvable</div>
          } />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/*" element={
          <PrivateRoute>
            <AppShell />
          </PrivateRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
}
